import { useQuery } from '@tanstack/react-query';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { catalogRepo } from '@/data/repositories/catalog-repo';
import { fmt, fmtDateTime, fillWindow } from '@/ui/utils/format';
import { normalizeOrderState, orderState, orderTotalCost, type Order } from '@/domain/types';
import { config } from '@/config';

export function OrderDetailScreen({ orderId }: { orderId: string }) {
  const id = String(orderId);
  const q = useQuery({
    queryKey: ['orders', 1],
    queryFn: () => catalogRepo.orders(1, 100),
    refetchInterval: config.orders.activePollIntervalMs,
  });
  const order = (q.data?.orders ?? []).find(o =>
    String((o as { id?: number }).id ?? '') === id ||
    String(o.startedat ?? '') === id
  );

  if (q.isPending) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner/></div>;
  if (q.isError) return <ErrorBox message="Couldn't load order." onRetry={() => q.refetch()}/>;
  if (!order) return <ErrorBox message="Order not found."/>;

  return <OrderDetailView o={order}/>;
}

function OrderDetailView({ o }: { o: Order }) {
  const state = orderState(o);
  const rawState = normalizeOrderState(o.state);
  const isDone = state === 'done';
  const isPendingReview = rawState === 'pendingreview';
  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar
        title={`#${(o as { id?: number }).id ?? ''}`}
        sub={o.reference?.name}
        onBack={() => history.back()}
        right={<button style={btnIcon}>{sized(Icons.share, 18)}</button>}
      />

      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{
          padding: 18, borderRadius: 20,
          background: isDone
            ? 'linear-gradient(135deg, rgba(57,229,140,0.10), rgba(57,229,140,0.02))'
            : state === 'cancelled'
              ? 'linear-gradient(135deg, rgba(255,90,110,0.10), rgba(255,90,110,0.02))'
              : 'linear-gradient(135deg, rgba(255,181,71,0.10), rgba(255,181,71,0.02))',
          border: `1px solid ${
            isDone ? 'rgba(57,229,140,0.25)'
            : state === 'cancelled' ? 'rgba(255,90,110,0.25)'
            : 'rgba(255,181,71,0.25)'}`,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14,
            background: isDone ? T.ok : state === 'cancelled' ? T.danger : T.warn,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: T.accentInk, flexShrink: 0,
          }}>{sized(isDone ? Icons.check : Icons.spark, 24, 2.5)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700,
              color: isDone ? T.ok : state === 'cancelled' ? T.danger : T.warn,
              textTransform: 'uppercase', letterSpacing: 1.2 }}>
              {isDone ? 'Finished' : state === 'cancelled' ? 'Cancelled' : isPendingReview ? 'Pending review' : 'In progress'}
            </div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
              {isDone ? 'Delivered' : isPendingReview ? 'Waiting for admin approval' : `${o.progress ?? 0}% complete`}
            </div>
          </div>
        </div>

        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <Row label="Service" value={o.reference?.name ?? ''}/>
          <Row label="Amount" value={`${fmt(o.amount ?? 0)} units`} mono/>
          <Row label="Rate" value={`${o.reference?.costper ?? 0} C / unit`} mono/>
          {!!o.reference?.filltime && <Row label="Fill window" value={fillWindow(o.reference.filltime)}/>}
          {o.platform && <Row label="Platform" value={o.platform}/>}
          <Row label="Created" value={fmtDateTime(o.startedat ?? 0)} last/>
        </div>

        <div style={{ ...card, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1, fontWeight: 500 }}>Paid</div>
            <div style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>From wallet</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
            <span style={{ fontFamily: T.fontMono, fontSize: 28, fontWeight: 500,
              color: T.text, letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(orderTotalCost(o))}
            </span>
            <span style={{ color: T.accent, fontSize: 13, fontWeight: 600 }}>C</span>
          </div>
        </div>

        {o.reference?.description && (
          <div style={{ ...card, padding: 18 }}>
            <div style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1, fontWeight: 500, marginBottom: 8 }}>Description</div>
            <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>
              {o.reference.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono, last }: { label: string; value: string; mono?: boolean; last?: boolean }) {
  return (
    <div style={{
      padding: '14px 18px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: 12,
      borderBottom: last ? 'none' : `1px solid ${T.line}`,
    }}>
      <span style={{ fontSize: 12, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 0.8, fontWeight: 500 }}>{label}</span>
      <span style={{
        fontSize: 13, color: T.text, fontWeight: 500,
        fontFamily: mono ? T.fontMono : T.font, textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: '60%',
      }}>{value}</span>
    </div>
  );
}
